<template>
  <button class="riseup-button"
          v-on="(disabled || loading) ? {} : { click: action }"
          :class="[variant, size]"
          :disabled="disabled">
    <tiny-loader v-show="loading" :variant="tinyLoaderVariant"/>
    <div v-show="!loading" class="button-content" :class="{ slot: hasSlot }">
      <div class="button-icon" v-if="icon">
        <img :src="icon" alt="button icon"/>
      </div>
      <div class="button-titles" :class="bold ? 'bold' : ''">
        <div class="button-title">{{ title }}</div>
        <slot class="button-title"></slot>
        <div class="button-subtitle">{{ subtitle }}</div>
      </div>
    </div>
  </button>
</template>

<script>
import TinyLoader from './TinyLoader/TinyLoader.vue';
import { VARIANTS as TinyLoaderVariants } from './TinyLoader/TinyLoader.consts';

export default {
  name: 'RiseupButton',
  components: {
    TinyLoader,
  },
  props: {
    action: Function,
    title: String,
    subtitle: String,
    variant: {
      type: String,
      default: 'primary',
      validator: val => [
        'primary', 'secondary', 'secondary-black', 'flat', 'flat-black', 'warning', 'danger', 'success', 'neutral',
        'discount', 'discount-secondary', 'fixed',
        'leumi', 'leumi-secondary', 'grayed', 'orange', 'light-green', 'green',
      ].includes(val),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'normal',
      validator: val => ['normal', 'small', 'slim'].includes(val),
    },
    icon: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    bold: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    tinyLoaderVariant() {
      return TinyLoaderVariants.includes(this.variant) ? this.variant : undefined;
    },
    hasSlot() {
      return !!this.$slots.default;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/riseup-colors';
@import "../scss/helpers";

.riseup-button {
  font-family: SimplerPro;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 88px;
  width: 248px;
  border-radius: 3px;
  cursor: pointer;
  border: none;

  .button-content {
    width: 100%;
    padding: 0 24px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    &.slot {
      padding: 0 10px;
      .button-titles {
        width: 100%;
      }
    }

    .button-icon {
      height: 22px;
      width: 22px;
      margin-left: 12px;
    }
    .button-titles {
      display: inline-flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .bold {
      font-weight: bold;
    }
  }

  &.small {
    width: 200px;
    height: 72px;
  }

  &.slim {
    width: 100%;
    height: 54px;

    .button-titles {
      .button-title {
        font-size: 18px;
        line-height: 18px;
      }
      .button-subtitle {
        font-size: 15px;
        line-height: 15px;
      }
    }
  }

  .button-titles {
    .button-title {
      @include width-100-to-avoid-text-wrapping;
      font-weight: bold;
      font-size: 20px;
      line-height: 20px;
    }

    .button-subtitle {
      @include width-100-to-avoid-text-wrapping;
      font-size: 16px;
      line-height: 16px;
    }
  }

  // 'name': [text-color, background-color, border-color]
  $colors: (
      'primary': [$riseup_white, $riseup_blue, null],
      'secondary': [$riseup_blue, $riseup_white, $riseup_blue],
      'flat': [$riseup_blue, $riseup_white, null],
      'flat-black': [$riseup_black, $riseup_white, null],
      'secondary-black': [$riseup_black, $riseup_white, $riseup_black],
      'warning': [$riseup_white, $riseup_yellow, null],
      'danger': [$riseup_white, $riseup_danger_red, null],
      'fixed': [$riseup_white, $riseup_red, null],
      'success': [$riseup_white, $riseup_green, null],
      'neutral': [$riseup_white, $riseup_black, null],
      'discount': [$riseup_white, $discount_green, null],
      'discount-secondary': [$discount_green, $riseup_white, $discount_green],
      'grayed': [$riseup_white, $riseup_gray_2, null],
      'orange': [$riseup_white, $riseup_orange, null],
      'leumi': [$riseup_white, $leumi_blue, null],
      'leumi-secondary': [$leumi_blue, $riseup_white, $leumi_blue],
      'light-green': [$riseup_dark_green, $riseup_light_green, null],
      'green': [$riseup_white, $riseup_dark_green, null],
  );

  @each $name, $color in $colors {
    &.#{$name} {
      color: nth($color, 1);
      background: nth($color, 2);
      border: if(nth($color, 3), 2px solid nth($color, 3), none);
      &:disabled {
        cursor: default;
        opacity: 0.4;
      }
    }
  }
}
</style>
